import React, {useEffect, useRef, useState} from 'react';
import {Checkbox, Col, Form, Input, message, Row} from 'antd';
import ProForm, {ProFormInstance} from '@ant-design/pro-form';
import {WdiCardTitle, WdiModalAppResult} from '@/components/WdiControls';
import {FileUploadUtility, WdiFileUpload} from '@/components/WdiControls/WdiFileUpload';
import {PageType} from '@/services/basic/pagetype';
import {Enum} from '@/enums/system';
import {ResponseStatus} from '@/utils/request';
import {FormPageProps} from '@/components/WdiBus/FormPage';
import DataStore from '@/components/GridWidget/DataStore';
import {ApplyFooter, ApplyFooterType} from '@/components/WdiBus/ApplyFooter';
import {getPageResult, initApplyPageContext, ViewEntityProps} from '../../components/data';
import {FormGoods, FormSp} from '@/services/sales/sp';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import FormSaInfosApp from '@/pages/sales/sa/components/FormSaInfos';
import FormSsInfosApp from '@/pages/sales/ss/components/FormSsInfos';
import FormGoodsForApplyPanel from './FormGoods';
import {FreeGoodsPaymentDetailsForApplyPanel} from "@/pages/sales/sp/components/detail";
import {WarningInfoAppProps} from "@/components/WdiBus/PanelForWarning";
import SPWarningInfoApp from "@/pages/sales/sp/components/WarningInformation";
import {SoldTo} from "@/services/mdm/soldto";
import {WdiEditor} from "@/components/WdiControls/WdiEditor";

const FreeGoodsPaymentApplyApp: React.FC<FormPageProps> = (props) => {

  const formRef = useRef<ProFormInstance>();
  const [entity, setEntity] = useState<ViewEntityProps>();
  const [pageType, setPageType] = useState<API.PageType>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [goodsDataSource, setGoodsDataSource] = useState<API.FormGoodsVo[]>([]);
  const [warningSettings, setWarningSettings] = useState<WarningInfoAppProps>({visible: false});
  const [editorRef,setEditorRef] = useState<any>()

  const onDetailsChange = (data: any) => {
    setDataSource(data);
  };

  const onFormGoodsChange = (data: any) => {
    setGoodsDataSource(data);
  };

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApplyPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;

        if (viewEntity.formSp) {
          formRef.current?.setFieldsValue({
            reimburseMaterials: viewEntity.formSp.reimburseMaterial?.split(','),
            remark: viewEntity.formSp.remark,
            fileInfo: FileUploadUtility.parse(viewEntity.formSp.fileName, viewEntity.formSp.filePath)
          });
          FormGoods.getForApply(viewEntity.formSp.formSpId || 0).then(responseResult => {
            setGoodsDataSource(DataStore.init(responseResult.data));
          })
        }
        if (viewEntity.paymentMethod) {
          formRef.current?.setFieldsValue({
            paymentMethodName: viewEntity.paymentMethod.name,
          });
        }
        if (viewEntity.soldToCode) {
          SoldTo.get(viewEntity.soldToCode).then((response) => {
            formRef.current?.setFieldsValue({
              soldTo: response.data.codeOrName,
            });
          });
        }
        if (viewEntity.pageTypeId) {
          PageType.get(viewEntity.pageTypeId).then((response) => {
            setPageType(response.data);
          });
        }

        if (viewEntity.formSpDetails) {
          setDataSource(DataStore.init(viewEntity.formSpDetails));
        }

        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && pageType && entity) {
      let title = "活动核销申请";
      if (entity.formSp) {
        title += '【编辑】'
      }
      props.setPageTitle(title);
    }
  }, [props.setPageTitle, pageType, entity]);

  return (
    <>
      <SPWarningInfoApp {...warningSettings} />
      <ProForm
        formRef={formRef}
        submitter={{
          render: () => {
            return (
              <ApplyFooter
                setLoading={props.setLoading}
                hiddenButton={[ApplyFooterType.Validate]}
                viewEntity={entity}
                submitBackUrl="/sales/sp/mypayment"
                doSubmit={async (type: number, forceSubmit: boolean) => {
                  if (!entity || !pageType) {
                    return;
                  }
                  const remark = await editorRef?.current?.getHtml?.();
                  const values = await formRef.current?.validateFieldsReturnFormatValue?.();
                  if (values && values.errorFields && values.errorFields.length) {
                    return;
                  }
                  const statusId = type === ApplyFooterType.Draft ? Enum.FormStatus.DRAFT : Enum.FormStatus.AWAITING;
                  values.remark = remark;
                  const req = getPageResult(values, entity, pageType, statusId, dataSource, [], [], goodsDataSource);
                  if (req.code !== ResponseStatus.SUCCESS) {
                    message.error(req.message);
                    return;
                  }
                  props?.setLoading?.(true);
                  return await FormSp.Buttons.apply({
                    forceSubmit: forceSubmit,
                    ...req.data
                  });
                }}
                del={async () => {
                  if (!entity || !entity.formId) {
                    return;
                  }
                  return await FormSp.Buttons.del(entity.formId);
                }}
                showWarning={(result: WdiModalAppResult, submitCallback: any) => {
                  setWarningSettings({
                    visible: true,
                    title: '警示信息',
                    wdiPanelList: result.data,
                    onClose: () => {
                      setWarningSettings({visible: false});
                    },
                    onSave: async () => {
                      return await submitCallback();
                    },
                  });
                }}
              />
            )
          }
        }}
      >
        {entity?.formSaId && (
          <FormSaInfosApp formRef={formRef} defaultCollapsed={true} formSaId={entity?.formSaId}/>
        )}
        {entity?.formSsId && (
          <FormSsInfosApp formRef={formRef} defaultCollapsed={true} formSsId={entity?.formSsId}/>
        )}
        <WdiPanel title={<WdiCardTitle iconType={Enum.IconType.FORM_SP} title='活动核销单信息'/>}>
          <Row gutter={[16, 0]}>
            <Col className="gutter-row" span={4}>
              <Form.Item name="paymentMethodName" label="支付方式">
                <Input readOnly/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="soldTo" label="客户">
                <Input readOnly/>
              </Form.Item>
            </Col>
            <Col className='gutter-row' span={16}>
              <Form.Item name='reimburseMaterials' label='核销支持文件'>
                <Checkbox.Group options={(entity?.reimburseMaterials || []).map((item) => ({
                  label: item.name,
                  value: String(item.value),
                }))}>
                </Checkbox.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <Form.Item name="remark" label="备注">
                <WdiEditor setRef={setEditorRef}/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                name="fileInfo"
                label="附件"
                valuePropName='fileList'
                getValueFromEvent={(e: any) => {
                  return e.fileList || [];
                }}
              >
                <WdiFileUpload leftTopInfo="请选择文件并上传(单个文件不能超500M)"/>
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        <FreeGoodsPaymentDetailsForApplyPanel
          dataSource={dataSource}
          data={{
            pageTypeId: entity?.pageTypeId,
            bohqId: dataSource?.[0]?.bohqId,
            soldToCode: entity?.soldToCode,
            formSaId: entity?.formSaId,
            paymentMethodId: entity?.paymentMethodId,
            ecFieldConfigs: entity?.ecFieldConfigs,
            goodsDataSource: goodsDataSource,
          }}
          onChange={onDetailsChange}
        />
        <FormGoodsForApplyPanel
          data={{
            pageTypeId: pageType?.pageTypeId,
            soldToCode: entity?.soldToCode,
            formSaId: entity?.formSaId,
            formSpDetails: dataSource,
          }}
          dataSource={goodsDataSource}
          onChange={onFormGoodsChange}/>
      </ProForm>
    </>
  );
};
export default FreeGoodsPaymentApplyApp;
